import React, { useRef, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { makeGetTask } from 'selectors/tasks';

import { updateTask } from 'actions/tasks';

import DateElement from 'components/date-element';

const TaskReminderPicker = ({ id, xAlign, defaultXPosition, showQuickOptions, inline, autoShow, withTitle = true, taskReminder, onChange, onShow, onHide, onComplete, onCancel, provideController, children }) => {
    const dateCtrl = useRef(null);
    const { t } = useTranslation('task');

    const update = useCallback(({ nextDate }) => {
        updateTask(id, {
            reminder: nextDate
        });
    }, [id]);

    const change = useCallback(nextDate => {
        const nextDateFormatted = nextDate && moment(nextDate).toISOString();

        if (taskReminder === nextDateFormatted) {
            return;
        }

        const nextProps = {
            nextDate: nextDateFormatted
        };

        if (onChange) {
            onChange(nextProps);
            return;
        }

        update(nextProps);
    }, [taskReminder, onChange, update]);

    const handleChange = useCallback(nextDate => {
        change(nextDate);

        onComplete && onComplete();

        dateCtrl.current?.hide();
    }, [dateCtrl, change, onComplete]);

    const handleRemove = useCallback(() => {
        const nextProps = {
            nextDate: null
        };

        if (onChange) {
            onChange(nextProps);
            return;
        }

        update(nextProps);

        onComplete && onComplete();

        dateCtrl.current?.hide();
    }, [onChange, dateCtrl, update, onComplete]);

    useEffect(() => {
        const ctrl = {
            show: colons => {
                dateCtrl.current?.show();
            },
            hide: () => {
                dateCtrl.current?.hide();
            },
            save: () => {
                change(dateCtrl.current?.getSelected());
            }
        };

        if (provideController) {
            if ('current' in provideController) {
                provideController.current = ctrl;
            } else {
                provideController && provideController(ctrl);
            }
        }

        return () => {
            if (provideController) {
                if ('current' in provideController) {
                    provideController.current = null;
                } else {
                    provideController && provideController(null);
                }
            }
        };
    }, []);

    return (
        <DateElement
            xAlign={xAlign}
            defaultXPosition={defaultXPosition}
            showQuickOptions={showQuickOptions}
            inline={inline}
            autoShow={autoShow}

            selected={taskReminder}
            title={withTitle && (taskReminder ? t('actions.reminder.titleChange') : t('actions.reminder.title'))}
            removePopoutTitle={ t('actions.reminder.remove.title') }
            removeBodyText={ t('actions.reminder.remove.description') }
            showTimeSelect={true}
            minDate={moment().startOf('hour').toDate().getTime()}

            onChange={handleChange}
            onRemove={handleRemove}
            onShow={onShow}
            onHide={onHide}
            onCancel={onCancel}
            provideController={dateCtrl}
        >
            { children || null }
        </DateElement>
    )

}

const makeMapStateToProps = () => {
    const getTask = makeGetTask();

    return ({ tasks, milestones }, ownProps) => {
        const task = getTask(tasks.items, ownProps.id);

        return {
            taskReminder: task?.reminder
        }
    }
}

export default connect(makeMapStateToProps)(React.memo(TaskReminderPicker));
